import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Pages
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import AdminPanel from "@/pages/AdminPanel";
import Profile from "@/pages/Profile";
import BrandedDocPage from "@/pages/BrandedDocPage";
import PollenDotsDashboard from "@/pages/PollenDotsDashboard";
import PollenDotsAddTour from "@/pages/PollenDotsAddTour";
import PollenDotsTourDetail from "@/pages/PollenDotsTourDetail";
import PollenDotsTours from "@/pages/PollenDotsTours";
import PollenDotsCustomers from "@/pages/PollenDotsCustomers";
import PollenDotsCustomerProfile from "@/pages/PollenDotsCustomerProfile";
import PollenDotsVendors from "@/pages/PollenDotsVendors";
import PollenDotsVendorDetail from "@/pages/PollenDotsVendorDetail";
import PollenDotsPayments from "@/pages/PollenDotsPayments";
import PollenDotsFinancials from "@/pages/PollenDotsFinancials";
import PollenDotsTeam from "@/pages/PollenDotsTeam";
import PollenDotsTeamMemberProfile from "@/pages/PollenDotsTeamMemberProfile";
import PollenDotsMyProfile from "@/pages/PollenDotsMyProfile";
import PollenDotsLocations from "@/pages/PollenDotsLocations";
import PollenDotsCelebrations from "@/pages/PollenDotsCelebrations";
import PollenDotsCare from "@/pages/PollenDotsCare";
import PollenDotsConcepts from "@/pages/PollenDotsConcepts";
import PublicConceptPage from "@/pages/PublicConceptPage";
import PollenDotsAlerts from "@/pages/PollenDotsAlerts";
import PollenDotsQueries from "@/pages/PollenDotsQueries";
import PollenDotsQueryWorkspace from "@/pages/PollenDotsQueryWorkspace";
import BrochureViewer from "@/pages/BrochureViewer";
import MemoriesContribute from "@/pages/MemoriesContribute";
import PublicJourneyShare from "@/pages/PublicJourneyShare";
import PublicNetworkShare from "@/pages/PublicNetworkShare";
import PublicVendorShare from "@/pages/PublicVendorShare";
import PublicItineraryShare from "@/pages/PublicItineraryShare";
import PublicZohoRedirect from "@/pages/PublicZohoRedirect";
import PublicCustomerUpload from "@/pages/PublicCustomerUpload";
import PollenDotsInvestors from "@/pages/PollenDotsInvestors";
import PublicInvestorPage from "@/pages/PublicInvestorPage";
import AdminSEO from "@/pages/admin/SEODashboard";
import AdminAEO from "@/pages/admin/AEODashboard";
import MailboxPage from "@/pages/admin/MailboxPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Global 401 interceptor — if ANY /api/ fetch returns 401 (expired/invalid
// session), wipe the local session token and fire a window event so the
// AuthProvider can flip the user to logged-out and the app redirects to
// /login. Previously expired sessions silently rendered empty dashboards
// (calendar marks missing, KPIs stuck at 0) because individual `res.ok`
// checks just skipped the data. This makes the failure mode loud and
// recovery one click away.
if (typeof window !== 'undefined' && !window.__POLLEN_FETCH_PATCHED__) {
  window.__POLLEN_FETCH_PATCHED__ = true;
  const _origFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const res = await _origFetch(input, init);
    try {
      const u = typeof input === 'string' ? input : (input && input.url) || '';
      // Only react to OUR backend; ignore third-party 401s (auth.emergent etc).
      if (res.status === 401 && BACKEND_URL && u.startsWith(BACKEND_URL) && u.includes('/api/')) {
        // Public endpoints (journey share, celebration cards, /api/w/*, /api/auth/me)
        // legitimately 401 for unauthenticated visitors — don't kick anyone.
        const PUBLIC_PATHS = ['/api/public/', '/api/w/', '/api/auth/me', '/api/auth/logout'];
        if (!PUBLIC_PATHS.some(p => u.includes(p))) {
          localStorage.removeItem('session_token');
          window.dispatchEvent(new Event('pollen:session-expired'));
        }
      }
    } catch (_) { /* never break the request */ }
    return res;
  };
}

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const headers = {};
      const savedToken = localStorage.getItem('session_token');
      if (savedToken) {
        headers['Authorization'] = `Bearer ${savedToken}`;
      }
      const response = await fetch(`${API}/auth/me`, {
        credentials: 'include',
        headers
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
        localStorage.removeItem('session_token');
      }
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // Global session-expired handler: the fetch interceptor above fires this
  // event whenever ANY authenticated /api/ call returns 401. Flip the user
  // to logged-out so the route guard sends them to /login. We avoid spamming
  // toasts if a burst of parallel requests all 401 at once.
  useEffect(() => {
    let recent = false;
    const onExpired = () => {
      if (recent) return;
      recent = true;
      setTimeout(() => { recent = false; }, 2000);
      setUser(null);
      try {
        import('sonner').then(({ toast }) => {
          toast.error('Your session expired. Please sign in again.');
        });
      } catch (_) { /* noop */ }
    };
    window.addEventListener('pollen:session-expired', onExpired);
    return () => window.removeEventListener('pollen:session-expired', onExpired);
  }, []);

  const logout = async () => {
    try {
      const headers = {};
      const savedToken = localStorage.getItem('session_token');
      if (savedToken) {
        headers['Authorization'] = `Bearer ${savedToken}`;
      }
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers
      });
    } catch (e) {
      console.error('Logout error:', e);
    }
    localStorage.removeItem('session_token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="loading-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// App Router Component
function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id - synchronous check
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/share/journey/:token" element={<PublicJourneyShare />} />
      <Route path="/share/crew/:token" element={<PublicVendorShare />} />
      <Route path="/share/network" element={<PublicNetworkShare />} />
      {/* New short-form share routes — proxied through pollendots.com/connection/*
          by ArtWorksIT's Nginx. Old /share/... routes stay for backward compat. */}
      <Route path="/connection/:token" element={<PublicJourneyShare />} />
      <Route path="/connection/crew/:token" element={<PublicVendorShare />} />
      <Route path="/connection/network" element={<PublicNetworkShare />} />
      <Route path="/thecaravannetwork" element={<PublicNetworkShare />} />
      {/* Public itinerary share — landing for CRM-generated PDFs sent to
          customers. Served on `pollendots.com/connection/itinerary/<slug>`
          because that namespace is already proxied to this React app. The
          shorter `/itinerary/<slug>` is kept as a friendly alias for the
          preview environment. */}
      <Route path="/connection/itinerary/:slug" element={<PublicItineraryShare />} />
      <Route path="/itinerary/:slug" element={<PublicItineraryShare />} />
      {/* Branded Pollen Dots digital brochure — public landing for hotel,
          query and tour brochures the operator builds inside the studio.
          Lives at the short `pollendots.com/connection/brochure/<slug>` URL. */}
      <Route path="/brochure/:slug" element={<BrochureViewer />} />
      <Route path="/connection/brochure/:slug" element={<BrochureViewer />} />
      <Route path="/contribute/:slug/:token" element={<MemoriesContribute />} />
      {/* Branded Zoho redirects — customer-facing wrappers around Zoho
          Books quotation / invoice URLs. */}
      <Route path="/connection/quote/:slug" element={<PublicZohoRedirect kind="quotation" />} />
      <Route path="/connection/invoice/:slug" element={<PublicZohoRedirect kind="invoice" />} />
      {/* Customer-facing document upload landing — operator generates this
          URL and shares it with the customer to collect ID proofs etc. */}
      <Route path="/connection/upload/:slug" element={<PublicCustomerUpload />} />
      <Route path="/invest/:slug" element={<PublicInvestorPage />} />
      <Route path="/concept/:slug" element={<PublicConceptPage />} />
      <Route path="/connection/concept/:slug" element={<PublicConceptPage />} />
      <Route path="/connection/invest/:slug" element={<PublicInvestorPage />} />
      <Route path="/connection/investor/:slug" element={<PublicInvestorPage />} />
      {/* Default landing → Pollen Dots dashboard (legacy `/dashboard` redirect kept for old links) */}
      <Route path="/" element={<Navigate to="/pollen-dots-dashboard" replace />} />
      <Route path="/dashboard" element={<Navigate to="/pollen-dots-dashboard" replace />} />
      <Route path="/admin" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />
      <Route path="/pollen-dots-dashboard" element={<ProtectedRoute><PollenDotsDashboard /></ProtectedRoute>} />
      <Route path="/pollen-dots/add-tour" element={<ProtectedRoute><PollenDotsAddTour /></ProtectedRoute>} />
      <Route path="/pollen-dots/tour/:id" element={<ProtectedRoute><PollenDotsTourDetail /></ProtectedRoute>} />
      <Route path="/pollen-dots/tours" element={<ProtectedRoute><PollenDotsTours /></ProtectedRoute>} />
      <Route path="/pollen-dots/customers" element={<ProtectedRoute><PollenDotsCustomers /></ProtectedRoute>} />
      <Route path="/pollen-dots/customer/:id" element={<ProtectedRoute><PollenDotsCustomerProfile /></ProtectedRoute>} />
      <Route path="/pollen-dots/vendors" element={<ProtectedRoute><PollenDotsVendors /></ProtectedRoute>} />
      <Route path="/pollen-dots/vendor/:id" element={<ProtectedRoute><PollenDotsVendorDetail /></ProtectedRoute>} />
      <Route path="/pollen-dots/payments" element={<ProtectedRoute><PollenDotsPayments /></ProtectedRoute>} />
      <Route path="/pollen-dots/financials" element={<ProtectedRoute><PollenDotsFinancials /></ProtectedRoute>} />
      <Route path="/pollen-dots/team" element={<ProtectedRoute><PollenDotsTeam /></ProtectedRoute>} />
      <Route path="/pollen-dots/team/:id" element={<ProtectedRoute><PollenDotsTeamMemberProfile /></ProtectedRoute>} />
      <Route path="/pollen-dots/team/:id/performance" element={<Navigate to="/pollen-dots/my" replace />} />
      {/* Legacy Pulse + Contributions routes are kept as redirects so older
          bookmarks / share links land on the unified Profile page. */}
      <Route path="/pollen-dots/performance" element={<Navigate to="/pollen-dots/my" replace />} />
      <Route path="/pollen-dots/contributions" element={<Navigate to="/pollen-dots/my" replace />} />
      <Route path="/pollen-dots/my" element={<ProtectedRoute><PollenDotsMyProfile /></ProtectedRoute>} />
      <Route path="/pollen-dots/profile/:userId" element={<ProtectedRoute><PollenDotsMyProfile /></ProtectedRoute>} />
      <Route path="/pollen-dots/profile/by-email/:email" element={<ProtectedRoute><PollenDotsMyProfile /></ProtectedRoute>} />
      <Route path="/pollen-dots/locations" element={<ProtectedRoute><PollenDotsLocations /></ProtectedRoute>} />
      <Route path="/pollen-dots/investors" element={<ProtectedRoute><PollenDotsInvestors /></ProtectedRoute>} />
      <Route path="/pollen-dots/concepts" element={<ProtectedRoute><PollenDotsConcepts /></ProtectedRoute>} />
      <Route path="/pollen-dots/seo" element={<ProtectedRoute><AdminSEO /></ProtectedRoute>} />
      <Route path="/pollen-dots/aeo" element={<ProtectedRoute><AdminAEO /></ProtectedRoute>} />
      <Route path="/pollen-dots/mailbox" element={<ProtectedRoute><MailboxPage /></ProtectedRoute>} />
      <Route path="/pollen-dots/celebrations" element={<ProtectedRoute><PollenDotsCelebrations /></ProtectedRoute>} />
      <Route path="/pollen-dots/care" element={<ProtectedRoute><PollenDotsCare /></ProtectedRoute>} />
      <Route path="/pollen-dots/alerts" element={<ProtectedRoute><PollenDotsAlerts /></ProtectedRoute>} />
      <Route path="/pollen-dots/queries" element={<ProtectedRoute><PollenDotsQueries /></ProtectedRoute>} />
      <Route path="/pollen-dots/queries/new" element={<ProtectedRoute><PollenDotsQueryWorkspace /></ProtectedRoute>} />
      <Route path="/pollen-dots/queries/:id" element={<ProtectedRoute><PollenDotsQueryWorkspace /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/:customerSlug/quotation/:docNumber" element={<BrandedDocPage />} />
      <Route path="/:customerSlug/invoice/:docNumber" element={<BrandedDocPage />} />
      <Route path="/:customerSlug/receipt/:docNumber" element={<BrandedDocPage />} />
      <Route path="/:customerSlug/credit-note/:docNumber" element={<BrandedDocPage />} />
      <Route path="/:customerSlug/debit-note/:docNumber" element={<BrandedDocPage />} />
      <Route path="*" element={<Navigate to="/pollen-dots-dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
export { API, BACKEND_URL };
